import React, { useState, useEffect } from 'react';
import superAdminApiClient from '../../../api/superAdminApiClient';
import { toast } from 'react-toastify';
import { Card, Row, Col, Form, Button } from 'react-bootstrap';

const MetaTagsSettings = () => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    const [metaTags, setMetaTags] = useState({
        header_css: '',
        header_script: '',
        footer_css: '',
        footer_script: ''
    });

    useEffect(() => {
        fetchMetaSettings();
    }, []);

    const fetchMetaSettings = async () => {
        setLoading(true);
        try {
            const response = await superAdminApiClient.get('/superadmin/settings');
            if (response.data.success) {
                const settingsList = response.data.settings || [];
                const extracted = {
                    header_css: '',
                    header_script: '',
                    footer_css: '',
                    footer_script: ''
                };
                settingsList.forEach(s => {
                    if (s.key in extracted) {
                        extracted[s.key] = s.value || '';
                    }
                });
                setMetaTags(extracted);
            } else {
                toast.error("Failed to fetch settings");
            }
        } catch (error) {
            toast.error("Error loading settings");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setMetaTags(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            // Save each key-value pair to the settings endpoint sequentially
            const keys = Object.keys(metaTags);
            for (const key of keys) {
                await superAdminApiClient.post('/superadmin/settings', {
                    key,
                    value: metaTags[key]
                });
            }
            toast.success("Meta Tags and custom scripts updated successfully!");
        } catch (error) {
            toast.error("Failed to save some settings. Please try again.");
            console.error("Save error:", error);
        } finally {
            setSubmitting(false);
        }
    };

    // Styling constants for a gorgeous dark-themed code editor look
    const textareaStyle = {
        fontFamily: "'Fira Code', 'Courier New', Courier, monospace",
        backgroundColor: '#181824',
        color: '#c5c9db',
        border: '1px solid #2e2e48',
        padding: '16px',
        borderRadius: '8px',
        fontSize: '14px',
        lineHeight: '1.6',
        resize: 'vertical',
        outline: 'none',
        boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.16)'
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center py-5" style={{ minHeight: '50vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-2">
            <Row className="justify-content-center">
                <Col lg={11} xl={10}>
                    {/* Page Title */}
                    <div className="d-sm-flex align-items-center justify-content-between mb-4">
                        <div>
                            <h4 className="mb-1 text-dark font-weight-bold">Custom Meta Tags & Scripts</h4>
                            <p className="text-muted mb-0 fs-13">Inject custom stylesheets, tracking codes, fonts, and scripts globally into the frontend application.</p>
                        </div>
                    </div>

                    <Form onSubmit={handleSubmit}>
                        {/* 1st Section: Header Section */}
                        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '12px', background: '#ffffff' }}>
                            <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="p-2 bg-primary-subtle text-primary rounded-3">
                                        <i className="ri-code-box-line fs-20"></i>
                                    </div>
                                    <div>
                                        <h5 className="mb-0 text-dark font-weight-bold">1. Header Injection Section</h5>
                                        <small className="text-muted">Injected directly inside the <code>&lt;head&gt;</code> tag of all public frontend pages.</small>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row>
                                    <Col md={12} className="mb-4">
                                        <Form.Group>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <Form.Label className="fs-14 font-weight-semibold text-dark mb-0">Header CSS</Form.Label>
                                                <span className="badge bg-info-subtle text-info fs-11">Auto-wrapped in &lt;style&gt; if missing</span>
                                            </div>
                                            <Form.Control
                                                as="textarea"
                                                rows={8}
                                                name="header_css"
                                                value={metaTags.header_css}
                                                onChange={handleInputChange}
                                                placeholder="/* Enter custom CSS here (e.g. body { background: #f4f6fa; }) */"
                                                style={textareaStyle}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <Form.Label className="fs-14 font-weight-semibold text-dark mb-0">Header Script (jQuery / JS)</Form.Label>
                                                <span className="badge bg-warning-subtle text-warning fs-11">Auto-wrapped in &lt;script&gt; if missing</span>
                                            </div>
                                            <Form.Control
                                                as="textarea"
                                                rows={8}
                                                name="header_script"
                                                value={metaTags.header_script}
                                                onChange={handleInputChange}
                                                placeholder="<!-- Google Analytics, Facebook Pixel, or Custom Javascript -->"
                                                style={textareaStyle}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* 2nd Section: Footer Section */}
                        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '12px', background: '#ffffff' }}>
                            <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="p-2 bg-success-subtle text-success rounded-3">
                                        <i className="ri-terminal-window-line fs-20"></i>
                                    </div>
                                    <div>
                                        <h5 className="mb-0 text-dark font-weight-bold">2. Footer Injection Section</h5>
                                        <small className="text-muted">Injected inside the <code>&lt;footer&gt;</code> tag of all public frontend pages.</small>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row>
                                    <Col md={12} className="mb-4">
                                        <Form.Group>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <Form.Label className="fs-14 font-weight-semibold text-dark mb-0">Footer CSS</Form.Label>
                                                <span className="badge bg-info-subtle text-info fs-11">Auto-wrapped in &lt;style&gt; if missing</span>
                                            </div>
                                            <Form.Control
                                                as="textarea"
                                                rows={8}
                                                name="footer_css"
                                                value={metaTags.footer_css}
                                                onChange={handleInputChange}
                                                placeholder="/* Enter custom CSS for footer components here */"
                                                style={textareaStyle}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <Form.Label className="fs-14 font-weight-semibold text-dark mb-0">Footer Script (jQuery / JS)</Form.Label>
                                                <span className="badge bg-warning-subtle text-warning fs-11">Auto-wrapped in &lt;script&gt; if missing</span>
                                            </div>
                                            <Form.Control
                                                as="textarea"
                                                rows={8}
                                                name="footer_script"
                                                value={metaTags.footer_script}
                                                onChange={handleInputChange}
                                                placeholder="<!-- Custom footer behavior tracking scripts or Chat widgets -->"
                                                style={textareaStyle}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Submit Button */}
                        <div className="d-flex justify-content-end gap-2 mb-5">
                            <Button 
                                type="submit" 
                                variant="primary" 
                                className="px-5 py-2 font-weight-semibold d-flex align-items-center gap-2 shadow-sm"
                                disabled={submitting}
                                style={{ borderRadius: '8px', fontSize: '15px' }}
                            >
                                {submitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        Saving Changes...
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-save-line fs-18"></i> Save Meta Settings
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                </Col>
            </Row>
        </div>
    );
};

export default MetaTagsSettings;
